import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const STATUS_CONFIG = {
  achieved:  { label: "Achieved",  bg: "#22c55e18", border: "#22c55e44", text: "#22c55e" },
  on_track:  { label: "On Track",  bg: "#2563EB18", border: "#2563EB44", text: "#60a5fa" },
  at_risk:   { label: "At Risk",   bg: "#f59e0b18", border: "#f59e0b44", text: "#f59e0b" },
  missed:    { label: "Missed",    bg: "#DC262618", border: "#DC262644", text: "#DC2626" },
  unknown:   { label: "Unknown",   bg: "#64748b18", border: "#64748b44", text: "#64748b" },
};

const METRICS     = ["activeUsers", "newUsers", "sessions", "pageviews", "bounceRate", "engagementRate", "followers", "revenue"];
const PERIODS     = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const PLATFORMS   = ["all", "newsday", "zimind", "standard", "southerneye", "hstv", "icwt"];

const EMPTY_FORM = { title: "", metric: "activeUsers", platform: "all", target: "", current: "0", period: "monthly", periodStart: "", periodEnd: "", notes: "" };

export function GoalsPage({ theme: t, isMobile, user }) {
  const [goals,   setGoals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [msg,     setMsg]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [formErr, setFormErr] = useState("");

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/api/goals`);
      if (!res.ok) throw new Error("Failed to load goals");
      setGoals(await res.json());
      setError("");
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() { setForm(EMPTY_FORM); setEditing(null); setFormErr(""); setShowForm(true); }
  function openEdit(g)  {
    setForm({ title: g.title, metric: g.metric, platform: g.platform, target: String(g.target), current: String(g.current), period: g.period, periodStart: g.periodStart?.slice(0,10) || "", periodEnd: g.periodEnd?.slice(0,10) || "", notes: g.notes || "" });
    setEditing(g.id);
    setFormErr("");
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title.trim())   { setFormErr("Title is required"); return; }
    if (!form.target)         { setFormErr("Target is required"); return; }
    if (!form.periodStart || !form.periodEnd) { setFormErr("Period dates are required"); return; }
    setSaving(true); setFormErr("");
    try {
      const method = editing ? "PUT" : "POST";
      const url    = editing ? `${API}/api/goals/${editing}` : `${API}/api/goals`;
      const res    = await fetchWithAuth(url, {
        method,
        body: JSON.stringify({ ...form, target: parseFloat(form.target), current: parseFloat(form.current || "0") }),
      });
      const d = await res.json();
      if (!res.ok) { setFormErr(d.error || "Save failed"); setSaving(false); return; }
      setMsg(`✓ Goal ${editing ? "updated" : "created"}.`);
      setShowForm(false);
      load();
    } catch { setFormErr("Network error"); }
    setSaving(false);
  }

  async function handleDelete(g) {
    if (!window.confirm(`Delete goal "${g.title}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API}/api/goals/${g.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg(`✓ Goal "${g.title}" deleted.`);
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Goals</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>{goals.length} goals tracked</p>
        </div>
        {isAdmin && (
          <button onClick={openCreate} style={{ background: "#2563EB", border: "none", borderRadius: 10, padding: "9px 18px", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "Georgia, serif" }}>
            + Add Goal
          </button>
        )}
      </div>

      {msg && (
        <div style={{ background: msg.startsWith("✓") ? "#22c55e11" : "#DC262611", border: `1px solid ${msg.startsWith("✓") ? "#22c55e33" : "#DC262633"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: msg.startsWith("✓") ? "#22c55e" : "#DC2626", display: "flex", justifyContent: "space-between" }}>
          <span>{msg}</span>
          <button onClick={() => setMsg("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Goal Form */}
      {showForm && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 22, marginBottom: 24 }}>
          <h4 style={{ margin: "0 0 16px", color: t.text, fontFamily: "Georgia, serif", fontWeight: 800 }}>{editing ? "Edit Goal" : "New Goal"}</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            {[
              { label: "Title", key: "title", type: "text", placeholder: "e.g. Reach 100k daily active users" },
              { label: "Target", key: "target", type: "number", placeholder: "e.g. 100000" },
              { label: "Current Value", key: "current", type: "number", placeholder: "Current metric value" },
              { label: "Notes", key: "notes", type: "text", placeholder: "Optional notes" },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>{f.label}</label>
                <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
            ))}
            {[
              { label: "Metric", key: "metric", options: METRICS },
              { label: "Platform", key: "platform", options: PLATFORMS },
              { label: "Period", key: "period", options: PERIODS },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>{f.label}</label>
                <select value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, cursor: "pointer" }}>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Period Start</label>
              <input type="date" value={form.periodStart} onChange={e => setForm(p => ({ ...p, periodStart: e.target.value }))}
                style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Period End</label>
              <input type="date" value={form.periodEnd} onChange={e => setForm(p => ({ ...p, periodEnd: e.target.value }))}
                style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
          {formErr && <div style={{ color: "#DC2626", fontSize: 13, marginTop: 12 }}>⚠️ {formErr}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ background: "#2563EB", border: "none", borderRadius: 8, padding: "9px 20px", color: "#fff", fontSize: 13, fontWeight: 800, cursor: saving ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
              {saving ? "Saving…" : "Save Goal"}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 16px", color: t.textSub, fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: t.textFaint }}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#DC2626", padding: 12 }}>⚠️ {error}</div>
      ) : goals.length === 0 ? (
        <div style={{ padding: "60px 0", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text, fontFamily: "Georgia, serif" }}>No goals yet</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4 }}>{isAdmin ? "Create your first goal above." : "No goals have been set. Ask an admin to create goals."}</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 18 }}>
          {goals.map(g => {
            const sc  = STATUS_CONFIG[g.status] || STATUS_CONFIG.unknown;
            const pct = Math.min(g.attainmentPct || 0, 100);
            return (
              <div key={g.id} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 4, fontFamily: "Georgia, serif" }}>{g.title}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 9, padding: "1px 7px", borderRadius: 10, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text, fontWeight: 700 }}>{sc.label}</span>
                      <span style={{ fontSize: 9, color: t.textFaint }}>{g.metric} · {g.platform} · {g.period}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => openEdit(g)} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "2px 8px", color: t.textSub, fontSize: 11, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDelete(g)} style={{ background: "none", border: "1px solid #DC262633", borderRadius: 5, padding: "2px 8px", color: "#DC2626", fontSize: 11, cursor: "pointer" }}>Del</button>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: t.textMuted }}>Progress</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: t.text }}>{pct}%</span>
                  </div>
                  <div style={{ background: t.surface2, borderRadius: 5, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 5, transition: "width 1s ease", background: g.status === "achieved" ? "#22c55e" : g.status === "at_risk" ? "#f59e0b" : g.status === "missed" ? "#DC2626" : "#2563EB" }} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Current", value: (g.current || 0).toLocaleString() },
                    { label: "Target",  value: (g.target  || 0).toLocaleString() },
                    { label: "Projected", value: g.projectedAttainment !== null ? `${g.projectedAttainment}%` : "—" },
                  ].map(kv => (
                    <div key={kv.label} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: t.text }}>{kv.value}</div>
                      <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase" }}>{kv.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
